export interface CityData {
  name: string;
  lat: number;
  lng: number;
}

export interface LocationData {
  slug: string;
  name: string;
  state: string;
  region: string;
  description: string;
  highlights: string[];
  content: string[];
  isPrimary: boolean;
  /** True for areas outside the 20-mile core radius that we still travel to. */
  extended?: boolean;
  lat: number;
  lng: number;
  cities: CityData[];
}

/**
 * Service areas within roughly 20 miles of 540 George Watson St, Orange Park, FL 32073,
 * plus two extended-radius areas the business also serves.
 */
export const locations: LocationData[] = [
  {
    slug: "orange-park",
    name: "Orange Park",
    state: "FL",
    region: "Clay County",
    isPrimary: true,
    lat: 30.1661,
    lng: -81.7065,
    description:
      "Liz Luxes Services Cleaning LLC is based on George Watson St in Orange Park, FL. We clean homes, offices, clinics, and rentals across Clay County seven days a week, 7 AM to 9 PM.",
    highlights: [
      "Based in Orange Park, FL 32073",
      "Homes, offices, clinics & short-term rentals",
      "Open 7 days a week, 7 AM – 9 PM",
      "Free written estimates",
    ],
    content: [
      "Orange Park is home base. Our office sits on George Watson St, which means we know the neighborhoods off Kingsley Ave, the Blanding Blvd corridor, Wells Rd, and the quiet streets tucked behind them. Being local keeps our travel time short and our scheduling flexible — including same-week bookings when a slot opens.",
      "Orange Park homeowners book us most often for deep cleaning, recurring weekly or bi-weekly service, and move in / move out cleans. On the commercial side we handle offices, clinics, and storefronts along Blanding and Park Ave, working before opening or after close so nobody's day gets interrupted.",
      "Call (904) 600-6466 for a free, no-pressure estimate anywhere in Orange Park.",
    ],
    cities: [
      { name: "Kingsley Ave", lat: 30.169, lng: -81.72 },
      { name: "Blanding Blvd", lat: 30.152, lng: -81.742 },
      { name: "Wells Road", lat: 30.176, lng: -81.727 },
      { name: "Loch Rane", lat: 30.14, lng: -81.75 },
    ],
  },
  {
    slug: "fleming-island",
    name: "Fleming Island",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 30.0936,
    lng: -81.7148,
    description:
      "Professional house and office cleaning in Fleming Island, FL. Deep cleans, recurring service, move in/out cleaning, and laundry — about ten minutes from our Orange Park office.",
    highlights: [
      "Under 10 minutes from our Orange Park base",
      "Weekly, bi-weekly & monthly plans",
      "Deep cleaning & move in/out service",
      "Repeat customer discounts",
    ],
    content: [
      "Fleming Island is one of our busiest areas. The larger family homes around Eagle Harbor, Pace Island, and Hibernia Rd are a natural fit for our recurring plans, where the same checklist gets run every visit so quality never drifts.",
      "We also handle the practical jobs that come with a growing community: move in / move out cleans for families relocating along US-17, post-renovation cleanups, and laundry service for households that would rather hand off the chore entirely.",
      "Ask about our recurring discount when you call (904) 600-6466 for your free Fleming Island estimate.",
    ],
    cities: [
      { name: "Eagle Harbor", lat: 30.096, lng: -81.733 },
      { name: "Pace Island", lat: 30.116, lng: -81.712 },
      { name: "Hibernia", lat: 30.05, lng: -81.7 },
      { name: "Doctors Lake", lat: 30.11, lng: -81.74 },
    ],
  },
  {
    slug: "oakleaf-plantation",
    name: "Oakleaf Plantation",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 30.1719,
    lng: -81.8215,
    description:
      "Cleaning services in Oakleaf Plantation, FL. Residential deep cleans, recurring house cleaning, closet organization, and laundry for one of Clay County's fastest-growing communities.",
    highlights: [
      "New-construction and post-move cleans",
      "Recurring weekly & bi-weekly service",
      "Closet and pantry organization",
      "Flexible evening and weekend slots",
    ],
    content: [
      "Oakleaf Plantation has grown fast, and a lot of our work here starts the same way: a family closes on a new build, moves in, and immediately needs the construction dust dealt with. Our deep clean covers the vents, baseboards, window tracks, and cabinet interiors where that dust settles.",
      "After that, most Oakleaf households move onto a recurring plan. We handle the weekly and bi-weekly cleaning, and plenty of clients add closet organization or laundry service to take a few more things off the list.",
      "Book a free Oakleaf Plantation estimate at (904) 600-6466.",
    ],
    cities: [
      { name: "Oakleaf Village", lat: 30.176, lng: -81.826 },
      { name: "Argyle Forest", lat: 30.21, lng: -81.77 },
      { name: "Chimney Lakes", lat: 30.213, lng: -81.79 },
      { name: "Bartram Springs", lat: 30.13, lng: -81.6 },
    ],
  },
  {
    slug: "middleburg",
    name: "Middleburg",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 30.0658,
    lng: -81.8998,
    description:
      "House cleaning and commercial cleaning in Middleburg, FL. Deep cleans, recurring service, and move in/out cleaning throughout western Clay County.",
    highlights: [
      "Single-family homes & acreage properties",
      "Deep cleaning and one-time resets",
      "Move in / move out cleaning",
      "Small business & office cleaning",
    ],
    content: [
      "Middleburg properties tend to be larger, with more square footage and more floor to cover than a typical suburban home. We quote by the actual scope rather than a flat rate, so you are never paying for rooms we aren't touching.",
      "We handle recurring house cleaning, one-time deep cleans, and move in / move out service across Middleburg, plus small offices and shops along Blanding Blvd and County Road 218.",
      "Call (904) 600-6466 for free Middleburg cleaning pricing.",
    ],
    cities: [
      { name: "Black Creek", lat: 30.06, lng: -81.87 },
      { name: "Ridgewood", lat: 30.08, lng: -81.88 },
      { name: "Tynes", lat: 30.04, lng: -81.92 },
      { name: "Lake Asbury", lat: 30.08, lng: -81.78 },
    ],
  },
  {
    slug: "green-cove-springs",
    name: "Green Cove Springs",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 29.9919,
    lng: -81.6784,
    description:
      "Cleaning services in Green Cove Springs, FL. Residential cleaning, office cleaning, clinic cleaning, and deep cleans for homes and businesses along the St. Johns River.",
    highlights: [
      "Historic and modern home cleaning",
      "Office, clinic & storefront cleaning",
      "Recurring and one-time service",
      "Free written estimates",
    ],
    content: [
      "Green Cove Springs mixes older riverfront homes with newer construction out toward Fleming Island, and both need a different touch. Our team is careful with original hardwood, tile, and fixtures in the historic district, and equally thorough in newer builds.",
      "On the commercial side we clean offices, clinics, and small businesses around the Walnut St and Orange Ave area, scheduling before opening or after close.",
      "Reach us at (904) 600-6466 for a free Green Cove Springs estimate.",
    ],
    cities: [
      { name: "Historic District", lat: 29.992, lng: -81.677 },
      { name: "Magnolia Point", lat: 30.01, lng: -81.71 },
      { name: "Penney Farms", lat: 29.976, lng: -81.809 },
      { name: "Russell", lat: 30.03, lng: -81.72 },
    ],
  },
  {
    slug: "doctors-inlet",
    name: "Doctors Inlet",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 30.1097,
    lng: -81.7737,
    description:
      "Home and office cleaning in Doctors Inlet, FL. Deep cleaning, recurring plans, laundry, and closet organization minutes from our Orange Park office.",
    highlights: [
      "Minutes from our Orange Park base",
      "Deep cleans & recurring house cleaning",
      "Laundry and organization add-ons",
      "Weekend availability",
    ],
    content: [
      "Doctors Inlet sits right between Orange Park and Fleming Island, which makes it one of the easiest areas for us to reach — often on short notice when a slot opens up in the schedule.",
      "Most of our Doctors Inlet work is residential: deep cleans, recurring weekly or bi-weekly service, and add-ons like laundry and closet organization for households juggling work and kids.",
      "Call (904) 600-6466 to book cleaning in Doctors Inlet.",
    ],
    cities: [
      { name: "Doctors Lake", lat: 30.116, lng: -81.75 },
      { name: "Swimming Pen Creek", lat: 30.1, lng: -81.79 },
      { name: "Lakeside", lat: 30.151, lng: -81.771 },
      { name: "Bellair", lat: 30.181, lng: -81.741 },
    ],
  },
  {
    slug: "lakeside",
    name: "Lakeside",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 30.1508,
    lng: -81.7712,
    description:
      "Cleaning services in Lakeside, FL. Residential deep cleaning, recurring plans, and move in/out cleaning just west of Orange Park.",
    highlights: [
      "Right next door to our Orange Park office",
      "Weekly, bi-weekly & monthly plans",
      "Move in / move out cleaning",
      "Repeat customer discounts",
    ],
    content: [
      "Lakeside is effectively our backyard. Being this close means tight scheduling windows, quick follow-ups, and the flexibility to fit in a same-week clean when something comes up.",
      "We handle recurring house cleaning for families throughout Lakeside, plus deep cleans and move in / move out service for the rental properties and townhomes off Blanding Blvd.",
      "Get free Lakeside cleaning pricing at (904) 600-6466.",
    ],
    cities: [
      { name: "Blanding Blvd", lat: 30.152, lng: -81.762 },
      { name: "Bellair", lat: 30.181, lng: -81.741 },
      { name: "Orange Park", lat: 30.166, lng: -81.706 },
      { name: "Doctors Inlet", lat: 30.11, lng: -81.774 },
    ],
  },
  {
    slug: "bellair-meadowbrook-terrace",
    name: "Bellair-Meadowbrook Terrace",
    state: "FL",
    region: "Clay County",
    isPrimary: false,
    lat: 30.1808,
    lng: -81.7412,
    description:
      "House cleaning in Bellair-Meadowbrook Terrace, FL. Deep cleans, recurring service, laundry, and move in/out cleaning for homes just north of Orange Park.",
    highlights: [
      "Established neighborhoods & older homes",
      "Deep cleaning specialists",
      "Laundry service available",
      "One-time or recurring service",
    ],
    content: [
      "Bellair-Meadowbrook Terrace is full of established homes, and older homes reward a proper deep clean — grout, baseboards, vents, and window tracks that have never really been touched make an enormous visual difference once they are.",
      "We work with homeowners here on both one-time deep cleans and ongoing recurring plans, and laundry service is a popular add-on for households that want the whole load handled in one visit.",
      "Call (904) 600-6466 for a free estimate in Bellair-Meadowbrook Terrace.",
    ],
    cities: [
      { name: "Meadowbrook", lat: 30.185, lng: -81.735 },
      { name: "Bellair", lat: 30.178, lng: -81.746 },
      { name: "Lakeside", lat: 30.151, lng: -81.771 },
      { name: "Ortega", lat: 30.257, lng: -81.714 },
    ],
  },
  {
    slug: "jacksonville",
    name: "Jacksonville",
    state: "FL",
    region: "Duval County",
    isPrimary: false,
    lat: 30.3322,
    lng: -81.6557,
    description:
      "Jacksonville, FL cleaning company. House cleaning, office cleaning, commercial cleaning, clinic cleaning, and Airbnb turnovers across Duval County.",
    highlights: [
      "Residential & commercial cleaning",
      "Office, clinic & storefront service",
      "Airbnb and short-term rental turnovers",
      "Before-hours and after-hours scheduling",
    ],
    content: [
      "Jacksonville is our largest service area by volume. We clean condos and apartments downtown, single-family homes across the Southside and Westside, offices, medical suites, and retail spaces — each on its own schedule.",
      "Commercial clients typically book us outside business hours so the space is ready before doors open. Short-term rental hosts book us between check-out and check-in, and we send photo confirmation once the property is guest-ready.",
      "Get your free Jacksonville cleaning quote at (904) 600-6466.",
    ],
    cities: [
      { name: "Downtown", lat: 30.328, lng: -81.656 },
      { name: "Southside", lat: 30.26, lng: -81.56 },
      { name: "Westside", lat: 30.29, lng: -81.75 },
      { name: "Southbank", lat: 30.316, lng: -81.66 },
    ],
  },
  {
    slug: "mandarin",
    name: "Mandarin",
    state: "FL",
    region: "Duval County",
    isPrimary: false,
    lat: 30.1666,
    lng: -81.6212,
    description:
      "Cleaning services in Mandarin, Jacksonville FL. Deep cleaning, recurring house cleaning, closet organization, and laundry for Mandarin homes and offices.",
    highlights: [
      "Deep cleaning & recurring plans",
      "Closet and pantry organization",
      "Laundry service available",
      "Office and clinic cleaning",
    ],
    content: [
      "Mandarin sits directly across the river from us, and it is one of our most requested Jacksonville neighborhoods. The larger homes off San Jose Blvd and Loretto Rd are well suited to our deep clean, which covers the detail work a routine tidy never gets to.",
      "Recurring service is popular here, often paired with closet organization or laundry. On the commercial side we handle offices and clinics along the San Jose corridor.",
      "Book a free Mandarin estimate at (904) 600-6466.",
    ],
    cities: [
      { name: "San Jose Blvd", lat: 30.18, lng: -81.62 },
      { name: "Loretto", lat: 30.148, lng: -81.617 },
      { name: "Julington Creek", lat: 30.123, lng: -81.622 },
      { name: "Beauclerc", lat: 30.196, lng: -81.616 },
    ],
  },
  {
    slug: "ortega",
    name: "Ortega",
    state: "FL",
    region: "Duval County",
    isPrimary: false,
    lat: 30.2569,
    lng: -81.7137,
    description:
      "Ortega, Jacksonville FL cleaning service. Detail-focused house cleaning, deep cleans, and recurring service for historic and waterfront homes.",
    highlights: [
      "Historic and waterfront home cleaning",
      "Careful handling of original surfaces",
      "Deep cleans and recurring plans",
      "Move in / move out service",
    ],
    content: [
      "Ortega's historic homes need a cleaner who pays attention. Original hardwood, tile, millwork, and fixtures all need the right products and a lighter hand than a modern build, and our team cleans accordingly.",
      "We handle recurring house cleaning, seasonal deep cleans, and move in / move out service throughout Ortega, Ortega Forest, and the surrounding riverfront streets.",
      "Call (904) 600-6466 for a free Ortega cleaning estimate.",
    ],
    cities: [
      { name: "Old Ortega", lat: 30.262, lng: -81.712 },
      { name: "Ortega Forest", lat: 30.246, lng: -81.72 },
      { name: "Venetia", lat: 30.276, lng: -81.72 },
      { name: "Avondale", lat: 30.302, lng: -81.7 },
    ],
  },
  {
    slug: "riverside-avondale",
    name: "Riverside-Avondale",
    state: "FL",
    region: "Duval County",
    isPrimary: false,
    lat: 30.308,
    lng: -81.696,
    description:
      "Cleaning services in Riverside and Avondale, Jacksonville FL. Historic home cleaning, apartment and condo cleaning, plus commercial service for local shops and restaurants.",
    highlights: [
      "Historic homes, condos & apartments",
      "Shop, café & restaurant cleaning",
      "Deep cleans and recurring plans",
      "After-hours commercial scheduling",
    ],
    content: [
      "Riverside and Avondale are a mix of historic homes, converted apartments, and one of Jacksonville's densest small-business districts — and we clean all three. Residential clients book deep cleans and recurring service; the shops and restaurants along King St and St. Johns Ave book us after close.",
      "Older buildings mean more detail work: original woodwork, tile, high baseboards, and windows that repay a proper clean. We plan the time accordingly rather than rushing a standard visit.",
      "Reach us at (904) 600-6466 for a free Riverside-Avondale estimate.",
    ],
    cities: [
      { name: "Five Points", lat: 30.315, lng: -81.684 },
      { name: "King Street", lat: 30.306, lng: -81.696 },
      { name: "St. Johns Ave", lat: 30.302, lng: -81.703 },
      { name: "Murray Hill", lat: 30.309, lng: -81.72 },
    ],
  },
  {
    slug: "san-marco",
    name: "San Marco",
    state: "FL",
    region: "Duval County",
    isPrimary: false,
    lat: 30.306,
    lng: -81.652,
    description:
      "San Marco, Jacksonville FL cleaning service. Condo and home cleaning, office cleaning, and commercial service for the San Marco Square business district.",
    highlights: [
      "Condos, townhomes & single-family homes",
      "Office and professional suite cleaning",
      "Restaurant & retail cleaning",
      "Evening and overnight availability",
    ],
    content: [
      "San Marco packs a lot into a small area: riverfront condos, historic homes, professional offices, and the restaurant and retail cluster around San Marco Square. We service all of it on schedules that fit each property type.",
      "Office and medical suites near the Southbank typically book recurring evening service. Residential clients book weekly or bi-weekly cleaning, with deep cleans scheduled seasonally.",
      "Book San Marco cleaning at (904) 600-6466.",
    ],
    cities: [
      { name: "San Marco Square", lat: 30.303, lng: -81.652 },
      { name: "Southbank", lat: 30.316, lng: -81.66 },
      { name: "St. Nicholas", lat: 30.297, lng: -81.635 },
      { name: "Empire Point", lat: 30.302, lng: -81.634 },
    ],
  },
  {
    slug: "argyle-forest",
    name: "Argyle Forest",
    state: "FL",
    region: "Duval County",
    isPrimary: false,
    lat: 30.21,
    lng: -81.77,
    description:
      "House cleaning in Argyle Forest, Jacksonville FL. Recurring cleaning, deep cleans, move in/out service, and laundry on Jacksonville's southwest side.",
    highlights: [
      "Family homes and townhome communities",
      "Recurring weekly & bi-weekly plans",
      "Move in / move out cleaning",
      "Laundry and organization add-ons",
    ],
    content: [
      "Argyle Forest is a short drive from our Orange Park office, which makes recurring scheduling easy and reliable here. Most of our clients are families on weekly or bi-weekly plans off Argyle Forest Blvd and Collins Rd.",
      "We also handle a steady stream of move in / move out cleans for the rental and townhome communities in the area, plus laundry and closet organization as add-ons.",
      "Call (904) 600-6466 for a free Argyle Forest estimate.",
    ],
    cities: [
      { name: "Chimney Lakes", lat: 30.213, lng: -81.79 },
      { name: "Collins Road", lat: 30.223, lng: -81.757 },
      { name: "Oakleaf", lat: 30.172, lng: -81.822 },
      { name: "Cecil", lat: 30.22, lng: -81.86 },
    ],
  },
  {
    slug: "fruit-cove",
    name: "Fruit Cove",
    state: "FL",
    region: "St. Johns County",
    isPrimary: false,
    lat: 30.1052,
    lng: -81.6237,
    description:
      "Cleaning services in Fruit Cove, FL. Deep cleaning, recurring house cleaning, closet organization, and laundry in northern St. Johns County.",
    highlights: [
      "Larger homes and gated communities",
      "Deep cleaning and recurring plans",
      "Closet organization & laundry",
      "Move in / move out service",
    ],
    content: [
      "Fruit Cove homes tend to be spacious, and space is exactly where a routine clean falls short. Our deep clean is built for it: every room, every baseboard, every vent, with time budgeted properly instead of squeezed.",
      "Recurring weekly and bi-weekly plans are the most popular option here, and many households add closet organization or laundry to make one visit cover more ground.",
      "Get free Fruit Cove pricing at (904) 600-6466.",
    ],
    cities: [
      { name: "Julington Creek", lat: 30.123, lng: -81.622 },
      { name: "Switzerland", lat: 30.053, lng: -81.618 },
      { name: "Bartram", lat: 30.09, lng: -81.55 },
      { name: "Mandarin", lat: 30.167, lng: -81.621 },
    ],
  },
  {
    slug: "julington-creek",
    name: "Julington Creek",
    state: "FL",
    region: "St. Johns County",
    isPrimary: false,
    lat: 30.123,
    lng: -81.622,
    description:
      "Julington Creek, FL cleaning service. Residential deep cleaning, recurring plans, Airbnb turnovers, and move in/out cleaning in St. Johns County.",
    highlights: [
      "Recurring residential cleaning",
      "Airbnb & short-term rental turnovers",
      "Move in / move out cleaning",
      "Repeat customer discounts",
    ],
    content: [
      "Julington Creek Plantation and the neighborhoods around it are a steady part of our weekly route. Families here mostly book recurring cleaning, with a deep clean scheduled once or twice a year as a reset.",
      "We also handle short-term rental turnovers in the area, working between check-out and check-in with fresh linens, restocking, and photo confirmation once the property is staged.",
      "Call (904) 600-6466 for a free Julington Creek estimate.",
    ],
    cities: [
      { name: "Julington Creek Plantation", lat: 30.128, lng: -81.63 },
      { name: "Fruit Cove", lat: 30.105, lng: -81.624 },
      { name: "Durbin Crossing", lat: 30.09, lng: -81.53 },
      { name: "Mandarin", lat: 30.167, lng: -81.621 },
    ],
  },
  {
    slug: "st-johns",
    name: "St. Johns",
    state: "FL",
    region: "St. Johns County",
    isPrimary: false,
    lat: 30.07,
    lng: -81.54,
    description:
      "Cleaning services in St. Johns, FL. Deep cleaning, recurring house cleaning, and new-construction cleanup across the fast-growing 32259 area.",
    highlights: [
      "New-construction and post-move cleans",
      "Recurring weekly & bi-weekly plans",
      "Closet organization & laundry",
      "Office and clinic cleaning",
    ],
    content: [
      "St. Johns is one of the fastest-growing parts of the region, and new builds bring a very specific kind of mess — drywall dust in the vents, film on the windows, and residue in every cabinet. Our deep clean handles all of it before you move in.",
      "Once you're settled, recurring service keeps it that way. We run weekly, bi-weekly, and monthly plans throughout the St. Johns and Durbin Crossing area, plus offices and clinics along CR-210.",
      "Book a free St. Johns estimate at (904) 600-6466.",
    ],
    cities: [
      { name: "Durbin Crossing", lat: 30.09, lng: -81.53 },
      { name: "Aberdeen", lat: 30.04, lng: -81.55 },
      { name: "RiverTown", lat: 30.0, lng: -81.6 },
      { name: "CR-210", lat: 30.03, lng: -81.5 },
    ],
  },
  {
    slug: "switzerland",
    name: "Switzerland",
    state: "FL",
    region: "St. Johns County",
    isPrimary: false,
    lat: 30.053,
    lng: -81.618,
    description:
      "House cleaning in Switzerland, FL. Deep cleans, recurring service, laundry, and organization for riverfront and acreage homes along SR-13.",
    highlights: [
      "Riverfront and acreage properties",
      "Deep cleaning & recurring plans",
      "Laundry and closet organization",
      "Move in / move out cleaning",
    ],
    content: [
      "The homes along SR-13 through Switzerland are some of the largest we service, and we quote them on actual scope rather than a flat rate so the price matches the work.",
      "Most clients here start with a full deep clean and then move to a recurring schedule, often adding laundry service so linens and everyday loads are handled in the same visit.",
      "Call (904) 600-6466 for free Switzerland cleaning pricing.",
    ],
    cities: [
      { name: "SR-13 Corridor", lat: 30.06, lng: -81.62 },
      { name: "Fruit Cove", lat: 30.105, lng: -81.624 },
      { name: "RiverTown", lat: 30.0, lng: -81.6 },
      { name: "Orangedale", lat: 30.02, lng: -81.63 },
    ],
  },
  {
    slug: "st-augustine",
    name: "St. Augustine",
    state: "FL",
    region: "St. Johns County",
    isPrimary: false,
    extended: true,
    lat: 29.8947,
    lng: -81.3145,
    description:
      "Extended service area: cleaning in St. Augustine, FL. Airbnb and vacation rental turnovers, deep cleans, and recurring residential and commercial cleaning.",
    highlights: [
      "Extended service area — advance booking",
      "Airbnb & vacation rental turnovers",
      "Deep cleaning and recurring plans",
      "Commercial and office cleaning",
    ],
    content: [
      "St. Augustine sits outside our 20-mile core radius, but it is a regular part of our route and we're glad to travel for it — we just ask for a little more notice when scheduling.",
      "Vacation rental turnovers are the bulk of our work here. In a town where guest reviews decide the next booking, we handle the full turnover: clean, fresh linens, restock, staging, and photo confirmation. We also serve year-round residents and local businesses with deep cleans and recurring service.",
      "Call (904) 600-6466 to schedule cleaning in St. Augustine.",
    ],
    cities: [
      { name: "Historic District", lat: 29.892, lng: -81.313 },
      { name: "Anastasia Island", lat: 29.87, lng: -81.28 },
      { name: "St. Augustine Beach", lat: 29.851, lng: -81.264 },
      { name: "World Golf Village", lat: 29.96, lng: -81.44 },
    ],
  },
  {
    slug: "fernandina-beach",
    name: "Fernandina Beach",
    state: "FL",
    region: "Nassau County",
    isPrimary: false,
    extended: true,
    lat: 30.6697,
    lng: -81.4626,
    description:
      "Extended service area: cleaning in Fernandina Beach, FL. Vacation rental turnovers, deep cleans, and recurring residential and commercial cleaning on Amelia Island.",
    highlights: [
      "Extended service area — advance booking",
      "Vacation rental & Airbnb turnovers",
      "Deep cleaning and recurring plans",
      "Commercial and office cleaning",
    ],
    content: [
      "Fernandina Beach and Amelia Island are outside our core 20-mile radius, but we serve them regularly for clients who book in advance.",
      "Short-term rentals drive most of our work here — full turnovers between guests with linens, restocking, and staging. We also handle deep cleans and recurring service for year-round residents, plus offices and shops around Centre St.",
      "Call (904) 600-6466 to schedule cleaning in Fernandina Beach.",
    ],
    cities: [
      { name: "Centre Street", lat: 30.671, lng: -81.464 },
      { name: "Amelia Island", lat: 30.61, lng: -81.44 },
      { name: "American Beach", lat: 30.575, lng: -81.44 },
      { name: "Yulee", lat: 30.632, lng: -81.606 },
    ],
  },
  {
    slug: "palm-coast",
    name: "Palm Coast",
    state: "FL",
    region: "Flagler County",
    isPrimary: false,
    lat: 29.5845,
    lng: -81.2078,
    description:
      "House and office cleaning in Palm Coast, FL. Deep cleans, recurring residential plans, move in/out cleaning, and vacation rental turnovers throughout Flagler County.",
    highlights: [
      "Residential deep cleans & recurring plans",
      "Move in / move out cleaning",
      "Vacation rental & Airbnb turnovers",
      "Free written estimates",
    ],
    content: [
      "Palm Coast is a mix of established canal-front homes, newer builds out toward Town Center, and seasonal properties near the beach — each one needing a slightly different approach. We quote by actual scope, so you only pay for the space we clean.",
      "Most Palm Coast households book recurring weekly or bi-weekly service, with deep cleans and move in / move out cleaning for anyone buying, selling, or relocating along Palm Coast Pkwy and Belle Terre Pkwy. Short-term rental owners use us for full guest turnovers with linens, restocking, and staging.",
      "Call (904) 600-6466 for a free Palm Coast cleaning estimate.",
    ],
    cities: [
      { name: "Town Center", lat: 29.545, lng: -81.209 },
      { name: "Palm Harbor", lat: 29.585, lng: -81.185 },
      { name: "Matanzas Woods", lat: 29.62, lng: -81.229 },
      { name: "Flagler Beach", lat: 29.475, lng: -81.128 },
    ],
  },
];

export const primaryLocation = locations.find((l) => l.isPrimary) ?? locations[0];

/** Areas inside the 20-mile core radius of the Orange Park office. */
export const coreLocations = locations.filter((l) => !l.extended);

/** Areas we travel to outside the 20-mile radius. */
export const extendedLocations = locations.filter((l) => l.extended);
